import { useQuery } from '@tanstack/react-query'
import { http } from '@/utils/httpCommon'
import { apiUrls } from '@/utils/apiUrls'
import { cloudWatchCountsApiResponseSchema, CloudWatchCountsApiResponse } from '@/types/cloudWatch.type'
import { cloudWatchQueryKeys } from './queryKeys'

interface UseCloudWatchCountsParams {
  workstream: string
  statusCodes?: '4xx' | '5xx' | 'all'
  startDate: string // Format: YYYY-MM-DD
  endDate: string // Format: YYYY-MM-DD
  environment?: string
  limit?: number
}

/**
 * Hook to fetch CloudWatch error counts
 * @param params - Query parameters including workstream, status codes, and date range
 * @returns CloudWatch counts data (400 errors, 500 errors, total errors)
 */
export const useCloudWatchCounts = ({
  workstream,
  statusCodes = 'all',
  startDate,
  endDate,
  environment,
  limit,
}: UseCloudWatchCountsParams) => {
  return useQuery({
    queryKey: cloudWatchQueryKeys.counts({
      workstream,
      statusCodes: statusCodes === 'all' ? null : statusCodes,
      startDate,
      endDate,
      environment: environment && environment !== 'all' ? environment : null,
    }),
    queryFn: async () => {
      // Build query params
      const params: Record<string, string | number> = {
        workstream,
        start_date: startDate,
        end_date: endDate,
      }

      // Add limit if specified
      if (limit) {
        params.limit = limit
      }

      // Add status_codes parameter
      if (statusCodes === 'all') {
        params.status_codes = '4xx,5xx'
      } else {
        params.status_codes = statusCodes
      }

      // Add environment if specified
      if (environment && environment !== 'all') {
        params.environment = environment
      }

      // Make API request using centralized apiUrls
      const { data } = await http.get<CloudWatchCountsApiResponse>(apiUrls.getCloudWatchCounts, { params })

      // Validate response with Zod
      const validatedData = cloudWatchCountsApiResponseSchema.parse(data)

      return validatedData
    },
    enabled: !!workstream && !!startDate && !!endDate,
  })
}
